import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  UserPlus, CalendarCheck, ShieldCheck, ScrollText, Shield, FolderOpen,
  ListTodo, Clock, ChevronRight, ArrowRight, CheckCircle2, Link2,
  Loader2, X, Play, Eye, Calendar, User, AlertTriangle, Save, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const ICON_MAP = {
  'user-plus': UserPlus,
  'calendar-check': CalendarCheck,
  'shield-check': ShieldCheck,
  'scroll-text': ScrollText,
  'shield': Shield,
  'folder-open': FolderOpen,
  'list': ListTodo,
};

const CATEGORY_COLORS = {
  onboarding: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  review: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' },
  compliance: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', dot: 'bg-indigo-500' },
  estate: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500' },
  insurance: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' },
  admin: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', dot: 'bg-gray-500' },
  custom: { bg: 'bg-[#F5F0EB]', border: 'border-[#E6E0D9]', text: 'text-[#4A3B32]', dot: 'bg-[#4A3B32]' },
};

const PRIORITY_STYLES = {
  High: 'bg-red-100 text-red-700',
  Medium: 'bg-amber-100 text-amber-700',
  Low: 'bg-green-100 text-green-700',
};

// ============ TEMPLATE CARD ============
function TemplateCard({ template, onPreview, onApply, onDelete }) {
  const catStyle = CATEGORY_COLORS[template.category] || CATEGORY_COLORS.custom;
  const IconComp = ICON_MAP[template.icon] || ListTodo;

  return (
    <div
      className={cn(
        "group rounded-xl border p-5 transition-all hover:shadow-md cursor-pointer",
        catStyle.border, catStyle.bg
      )}
      data-testid={`template-card-${template.id}`}
      onClick={() => onPreview(template)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", catStyle.bg, "border", catStyle.border)}>
          <IconComp className={cn("w-5 h-5", catStyle.text)} />
        </div>
        {template.isSystem ? (
          <Badge variant="outline" className="text-xs border-[#C6A88F] text-[#4A3B32]">System</Badge>
        ) : (
          <div className="flex items-center gap-1">
            <Badge variant="outline" className="text-xs border-[#C6A88F] text-[#4A3B32]">Custom</Badge>
            {onDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(template); }}
                className="p-1 rounded hover:bg-red-100 text-[#8C8078] hover:text-red-600 transition-colors"
                data-testid={`template-delete-${template.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>

      <h3 className="font-semibold text-[#2C2420] text-sm mb-1 group-hover:text-[#4A3B32]">
        {template.name}
      </h3>
      <p className="text-xs text-[#5D524D] line-clamp-2 mb-3">{template.description}</p>

      <div className="flex items-center gap-3 text-xs text-[#8C8078]">
        <span className="flex items-center gap-1">
          <ListTodo className="w-3.5 h-3.5" />
          {template.taskCount} tasks
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" />
          ~{template.estimatedDays}d
        </span>
      </div>

      <div className="flex items-center gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          size="sm"
          variant="outline"
          className="flex-1 h-8 text-xs"
          onClick={(e) => { e.stopPropagation(); onPreview(template); }}
          data-testid={`template-preview-${template.id}`}
        >
          <Eye className="w-3.5 h-3.5 mr-1" />
          Preview
        </Button>
        <Button
          size="sm"
          className="flex-1 h-8 text-xs caffeine-btn"
          onClick={(e) => { e.stopPropagation(); onApply(template); }}
          data-testid={`template-apply-${template.id}`}
        >
          <Play className="w-3.5 h-3.5 mr-1" />
          Apply
        </Button>
      </div>
    </div>
  );
}

// ============ TEMPLATE DETAIL MODAL ============
function TemplateDetailModal({ template, open, onClose, onApply }) {
  const [fullTemplate, setFullTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    if (open && template) {
      setLoading(true);
      api.get(token, `/task-templates/${template.id}`)
        .then(res => setFullTemplate(res.data))
        .catch(() => toast.error('Failed to load template details'))
        .finally(() => setLoading(false));
    } else {
      setFullTemplate(null);
    }
  }, [open, template, token]);

  if (!template) return null;

  const catStyle = CATEGORY_COLORS[template.category] || CATEGORY_COLORS.custom;
  const IconComp = ICON_MAP[template.icon] || ListTodo;
  const tasks = fullTemplate?.tasks || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="template-detail-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center border", catStyle.bg, catStyle.border)}>
              <IconComp className={cn("w-5 h-5", catStyle.text)} />
            </div>
            <div>
              <DialogTitle className="text-lg">{template.name}</DialogTitle>
              <DialogDescription className="text-sm mt-0.5">{template.description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex items-center gap-4 text-sm text-[#5D524D] border-b border-[#E6E0D9] pb-4">
          <span className="flex items-center gap-1"><ListTodo className="w-4 h-4" />{template.taskCount} tasks</span>
          <span className="flex items-center gap-1"><Clock className="w-4 h-4" />~{template.estimatedDays} days</span>
          <Badge variant="outline" className={cn("capitalize", catStyle.text, catStyle.border)}>{template.category}</Badge>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#4A3B32]" />
          </div>
        ) : (
          <div className="space-y-3 pt-2">
            <h4 className="text-sm font-semibold text-[#2C2420]">Workflow Steps</h4>
            {tasks.map((task, i) => {
              const depIdx = task.dependsOnIndex;
              const depName = depIdx !== null && depIdx !== undefined ? tasks.find(t => t.index === depIdx)?.title : null;
              return (
                <div key={i} className="rounded-lg border border-[#E6E0D9] bg-white p-4" data-testid={`template-task-${i}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#F0EBE6] flex items-center justify-center text-xs font-bold text-[#4A3B32]">
                        {i + 1}
                      </span>
                      <div>
                        <p className="font-medium text-sm text-[#2C2420]">{task.title}</p>
                        {task.description && <p className="text-xs text-[#5D524D] mt-0.5">{task.description}</p>}
                      </div>
                    </div>
                    <Badge className={cn("text-xs shrink-0", PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.Medium)}>
                      {task.priority}
                    </Badge>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 ml-8 text-xs text-[#8C8078]">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Day {task.daysFromStart}
                    </span>
                    <span className="capitalize px-1.5 py-0.5 rounded bg-[#F5F3F0]">{task.category}</span>
                    {depName && (
                      <span className="flex items-center gap-1 text-amber-600">
                        <Link2 className="w-3 h-3" /> Depends on: {depName}
                      </span>
                    )}
                  </div>

                  {task.subtasks?.length > 0 && (
                    <div className="ml-8 mt-2 grid grid-cols-2 gap-1">
                      {task.subtasks.map((st, j) => (
                        <span key={j} className="flex items-center gap-1.5 text-xs text-[#5D524D]">
                          <CheckCircle2 className="w-3 h-3 text-[#C6A88F]" />
                          {typeof st === 'string' ? st : st.title}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onClose(false)} data-testid="template-detail-close">Close</Button>
          <Button className="caffeine-btn" onClick={() => onApply(template)} data-testid="template-detail-apply">
            <Play className="w-4 h-4 mr-2" /> Apply Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ APPLY TEMPLATE MODAL ============
export function ApplyTemplateModal({ open, onClose, contextType, contextId, contextName, preSelectedTemplate }) {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [assignTo, setAssignTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  useEffect(() => {
    if (open) {
      setLoading(true);
      api.get(token, '/task-templates')
        .then(res => {
          const all = [...(res.data.system || []), ...(res.data.custom || [])];
          setTemplates(all);
          if (preSelectedTemplate) {
            setSelectedId(preSelectedTemplate.id);
          }
        })
        .catch(() => toast.error('Failed to load templates'))
        .finally(() => setLoading(false));
    }
  }, [open, token, preSelectedTemplate]);

  const selectedTemplate = templates.find(t => t.id === selectedId);

  const handleApply = async () => {
    if (!selectedId) {
      toast.error('Please select a template');
      return;
    }
    setApplying(true);
    try {
      const res = await api.post(token, '/task-templates/apply', {
        templateId: selectedId,
        contextType,
        contextId,
        startDate: startDate || null,
        assignTo: assignTo || null,
      });
      toast.success(`Created ${res.data.created} tasks from "${res.data.templateName}"`);
      onClose(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply template');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-lg" data-testid="apply-template-modal">
        <DialogHeader>
          <DialogTitle>Apply Workflow Template</DialogTitle>
          <DialogDescription>
            Auto-create a set of linked tasks for {contextName || contextType}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#4A3B32]" />
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium text-[#2C2420]">Select Template</Label>
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger className="mt-1" data-testid="template-select-trigger">
                  <SelectValue placeholder="Choose a workflow template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="flex items-center gap-2">
                        {t.name}
                        <span className="text-xs text-[#8C8078]">({t.taskCount} tasks, ~{t.estimatedDays}d)</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedTemplate && (
              <div className="rounded-lg border border-[#E6E0D9] bg-[#FDFCFB] p-3">
                <p className="text-xs text-[#5D524D]">{selectedTemplate.description}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-[#8C8078]">
                  <span className="flex items-center gap-1"><ListTodo className="w-3 h-3" />{selectedTemplate.taskCount} tasks</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />~{selectedTemplate.estimatedDays} days</span>
                  <button
                    className="ml-auto text-[#4A3B32] underline hover:no-underline text-xs"
                    onClick={() => setPreviewTemplate(selectedTemplate)}
                    data-testid="apply-modal-preview-link"
                  >
                    Preview tasks
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-[#2C2420]">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1"
                  data-testid="template-start-date"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#2C2420]">Assign To (optional)</Label>
                <Input
                  placeholder="Owner name"
                  value={assignTo}
                  onChange={(e) => setAssignTo(e.target.value)}
                  className="mt-1"
                  data-testid="template-assign-to"
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} data-testid="apply-template-cancel">Cancel</Button>
          <Button
            className="caffeine-btn"
            onClick={handleApply}
            disabled={!selectedId || applying}
            data-testid="apply-template-confirm"
          >
            {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            {applying ? 'Creating Tasks...' : 'Apply Template'}
          </Button>
        </DialogFooter>

        <TemplateDetailModal
          template={previewTemplate}
          open={!!previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onApply={() => {
            setPreviewTemplate(null);
            if (previewTemplate) setSelectedId(previewTemplate.id);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

// ============ SAVE AS TEMPLATE MODAL ============
export function SaveAsTemplateModal({ open, onClose, contextType, contextId, contextName }) {
  const { token } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('custom');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(contextName ? `${contextName} Workflow` : '');
      setDescription('');
      setCategory('custom');
    }
  }, [open, contextName]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(token, '/task-templates/from-tasks', {
        name: name.trim(),
        description: description.trim(),
        category,
        contextType,
        contextId,
      });
      const count = res.data.template?.taskCount || res.data.template?.tasks?.length || 0;
      toast.success(`Template "${name}" created with ${count} tasks`);
      onClose(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-md" data-testid="save-as-template-modal">
        <DialogHeader>
          <DialogTitle>Save as Workflow Template</DialogTitle>
          <DialogDescription>
            Create a reusable template from {contextName || 'this'} {contextType}'s current tasks
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm font-medium text-[#2C2420]">Template Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. New Client Onboarding v2"
              className="mt-1"
              data-testid="save-template-name"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-[#2C2420]">Description (optional)</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this workflow covers..."
              className="mt-1"
              data-testid="save-template-description"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-[#2C2420]">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1" data-testid="save-template-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Custom</SelectItem>
                <SelectItem value="onboarding">Onboarding</SelectItem>
                <SelectItem value="review">Review</SelectItem>
                <SelectItem value="compliance">Compliance</SelectItem>
                <SelectItem value="estate">Estate</SelectItem>
                <SelectItem value="insurance">Insurance</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} data-testid="save-template-cancel">Cancel</Button>
          <Button
            className="caffeine-btn"
            onClick={handleSave}
            disabled={!name.trim() || saving}
            data-testid="save-template-confirm"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ MAIN LIBRARY COMPONENT ============
export default function TaskTemplatesLibrary() {
  const { token } = useAuth();
  const [templates, setTemplates] = useState({ system: [], custom: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [applyTarget, setApplyTarget] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get(token, '/task-templates');
      setTemplates(res.data);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Delete custom template "${template.name}"?`)) return;
    try {
      await api.delete(token, `/task-templates/${template.id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const allTemplates = [...(templates.system || []), ...(templates.custom || [])];
  const filtered = filter === 'all' ? allTemplates
    : filter === 'system' ? (templates.system || [])
    : filter === 'custom' ? (templates.custom || [])
    : allTemplates.filter(t => t.category === filter);

  const categories = [...new Set(allTemplates.map(t => t.category))];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A3B32]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="task-templates-library">
      {/* Filter Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {['all', 'system', 'custom', ...categories].filter((v, i, a) => a.indexOf(v) === i).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              "px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all border",
              filter === f
                ? "bg-[#4A3B32] text-white border-[#4A3B32]"
                : "bg-white text-[#5D524D] border-[#E6E0D9] hover:border-[#4A3B32] hover:text-[#4A3B32]"
            )}
            data-testid={`template-filter-${f}`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Template Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-[#E6E0D9] rounded-xl">
          <ListTodo className="w-12 h-12 text-[#C6A88F] mx-auto mb-3" />
          <p className="text-[#5D524D]">No templates found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(t => (
            <TemplateCard
              key={t.id}
              template={t}
              onPreview={setPreviewTemplate}
              onApply={setApplyTarget}
              onDelete={!t.isSystem ? handleDeleteTemplate : undefined}
            />
          ))}
        </div>
      )}

      {/* Template Detail Preview */}
      <TemplateDetailModal
        template={previewTemplate}
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        onApply={(t) => { setPreviewTemplate(null); setApplyTarget(t); }}
      />
    </div>
  );
}
